import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { Circle, CircleMarker, MapContainer, TileLayer, Tooltip, useMap } from "react-leaflet";
import { latLng, latLngBounds } from "leaflet";
import "leaflet/dist/leaflet.css";
import { ExternalServiceArea } from "../../../types/externalServices";

// Each area keeps its colour in the list and on the map, so the two can be
// read against each other.
const AREA_COLORS = ["#1976d2", "#2e7d32", "#ed6c02", "#9c27b0", "#d32f2f", "#00838f", "#6d4c41", "#c2185b"];

export const getAreaColor = (index: number) => AREA_COLORS[index % AREA_COLORS.length];

const POLAND_CENTER: [number, number] = [52.07, 19.48];
const DEFAULT_ZOOM = 6;
// Framing waits until a modal has finished animating in and the radius stops
// changing, so dragging the slider doesn't make the map jump with every step.
const FIT_DELAY_MS = 400;

type AreaLike = Pick<ExternalServiceArea, "city" | "county" | "region" | "latitude" | "longitude" | "radius_km">;

/** Sizes the map once its container has settled and frames every circle. */
const FitToAreas = ({ areas }: { areas: AreaLike[] }) => {
  const map = useMap();
  const signature = areas
    .map((area) => `${area.latitude},${area.longitude},${area.radius_km}`)
    .join("|");

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      // Leaflet measures its container on mount, while a modal may still be
      // animating in - re-measure before framing, or the fit is computed
      // against the wrong size.
      map.invalidateSize();
      if (areas.length === 0) return;
      const bounds = latLngBounds([]);
      areas.forEach((area) => {
        // toBounds takes the square's full side, so twice the radius.
        bounds.extend(latLng(area.latitude, area.longitude).toBounds(area.radius_km * 2000));
      });
      map.fitBounds(bounds, { padding: [24, 24], maxZoom: 11 });
    }, FIT_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
    // The signature stands in for the areas themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, signature]);

  return null;
};

interface ExternalServiceAreasMapProps {
  areas: AreaLike[];
  height?: number | string;
}

/** The areas an external service works in, as circles on an OpenStreetMap map. */
export const ExternalServiceAreasMap = ({ areas, height = 320 }: ExternalServiceAreasMapProps) => {
  const { t } = useTranslation();
  const circles = useMemo(
    () => areas.map((area, index) => ({ area, color: getAreaColor(index) })),
    [areas]
  );

  return (
    <Box
      sx={{
        position: "relative",
        height,
        width: "100%",
        borderRadius: "var(--defaultRadius)",
        overflow: "hidden",
        border: 1,
        borderColor: "divider",
        // Leaflet's tooltips are always white, while the MUI text inside them
        // follows the theme - match them to the theme so they stay readable
        // in dark mode.
        "& .leaflet-tooltip": {
          backgroundColor: "background.paper",
          color: "text.primary",
          borderColor: "divider",
        },
        "& .leaflet-tooltip-top::before": {
          borderTopColor: (theme) => theme.palette.background.paper,
        },
      }}
    >
      {/* Sits inside a scrolling page or form, so the wheel scrolls the page
          rather than zooming the map - the zoom buttons do that. */}
      <MapContainer
        center={POLAND_CENTER}
        zoom={DEFAULT_ZOOM}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitToAreas areas={areas} />
        {circles.map(({ area, color }) => (
          <Circle
            key={`${area.city}|${area.county}|${area.region}`}
            center={[area.latitude, area.longitude]}
            radius={area.radius_km * 1000}
            pathOptions={{ color, weight: 2, fillColor: color, fillOpacity: 0.12 }}
          >
            <Tooltip direction="top" sticky>
              <Typography variant="body2" fontWeight={600} component="span">
                {area.city}
              </Typography>{" "}
              <Typography variant="body2" component="span">
                · {t("{{value}} km radius", { value: area.radius_km })}
              </Typography>
            </Tooltip>
          </Circle>
        ))}
        {circles.map(({ area, color }) => (
          <CircleMarker
            key={`center-${area.city}|${area.county}|${area.region}`}
            center={[area.latitude, area.longitude]}
            radius={4}
            pathOptions={{ color: "#ffffff", weight: 1.5, fillColor: color, fillOpacity: 1 }}
          />
        ))}
      </MapContainer>
      {areas.length === 0 && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 1000,
          }}
        >
          <Typography
            variant="body2"
            sx={{ bgcolor: "background.paper", color: "text.secondary", px: 1.5, py: 0.75, borderRadius: 1 }}
          >
            {t("No areas yet")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
